import { useState, useEffect, useCallback } from 'react';
import { categoryService } from '../../services';
import { Category, SubCategory, CategoryLocale } from '../../types/category.types';
import { toast } from 'react-toastify';

export interface DisplayCategory {
  id: string;
  name: string;
  image: string;
  TypesItems: string;
  vendorCount: number;
  subCategories?: SubCategory[]; // Store original subcategories for editing
  locale?: CategoryLocale;
}

const transformCategory = (category: Category): DisplayCategory => {
  const TypesItems = category.subCategories
    ?.map((subCat) => {
      // Get all type names in one line
      const typeNames = subCat.types.map((type) => type.name).join(', ');

      // Get all items from all types
      const allItems = subCat.types.flatMap((type) => type.items).join(', ');

      return `${subCat.name}: (${typeNames})\n${allItems}`;
    })
    .join('\n') || '';

    // Check if iconURL is a data URI, external URL, or needs placeholder
    const isDataURI = category.iconURL?.startsWith('data:');
    const isPlaceholderURL = !isDataURI && (category.iconURL?.includes('example.com') || category.iconURL?.includes('placeholder'));
    
    // Use the iconURL directly if it's valid, otherwise use placeholder
    let imageURL = category.iconURL;
    if (!imageURL || isPlaceholderURL) {
      imageURL = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0Y0RjRGNCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5DYXRlZ29yeTwvdGV4dD48L3N2Zz4=';
    }

    return {
      id: category.categoryId,
      name: category.name,
      image: imageURL,
      TypesItems,
      vendorCount: (category as any)?.vendorCount ?? 0,
      subCategories: category.subCategories || [], // Store for editing
      locale: category.locale,
    };
  };

export const useCategories = () => {
  const [categories, setCategories] = useState<DisplayCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.getAllCategories();
      console.log('📦 Categories API Response:', response);
      console.log('📦 Raw categories data:', response.data?.categories);
      
      const transformedCategories = response.data?.categories?.map(transformCategory) || [];
      console.log('📦 Transformed categories:', transformedCategories);
      setCategories(transformedCategories);
    } catch (err: any) {
      console.error('❌ Error fetching categories:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to fetch categories';
      setError(errorMessage);
      toast.error(errorMessage);
      setCategories([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const reorderCategories = useCallback(async (reorderedCategories: DisplayCategory[]) => {
    try {
      const reorderData = reorderedCategories.map((cat, index) => ({
        categoryId: cat.id,
        displayOrder: index + 1,
      }));

      await categoryService.reorderCategories({ categories: reorderData });
      toast.success('Categories reordered successfully');
      setCategories(reorderedCategories);
    } catch (err: any) {
      console.error('❌ Error reordering categories:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to reorder categories';
      toast.error(errorMessage);
      // Revert to original order
      fetchCategories();
    }
  }, [fetchCategories]);

  const deleteCategory = useCallback(async (categoryId: string) => {
    try {
      await categoryService.deleteCategory(categoryId);
      toast.success('Category deleted successfully');
      setCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
    } catch (err: any) {
      console.error('❌ Error deleting category:', err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || 'Failed to delete category';
      toast.error(errorMessage);
    }
  }, []);

  const createCategory = useCallback(async (data: {
    name: string;
    iconURL: string; // Required by backend
    displayOrder: number;
    subCategories?: Array<{
      name: string;
      displayOrder: number;
      isActive: boolean;
      types: Array<{
        name: string;
        displayOrder: number;
        isActive: boolean;
        description: string;
        items: string[];
      }>;
    }>;
  }) => {
    try {
      console.log('📤 Calling API with data:', JSON.stringify(data, null, 2));
      const response = await categoryService.createCategory(data);
      console.log('✅ Category created:', response);
      toast.success('Category created successfully');
      fetchCategories(); // Refresh the list
    } catch (err: any) {
      console.error('❌ Error creating category:', err);
      console.error('❌ Error response:', err?.response?.data);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || 'Failed to create category';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchCategories]);

  const updateCategory = useCallback(async (categoryId: string, data: {
    name?: string;
    iconURL?: string;
    subCategories?: any[];
  }) => {
    try {
      await categoryService.updateCategory(categoryId, data);
      toast.success('Category updated successfully');
      fetchCategories(); // Refresh the list
    } catch (err: any) {
      console.error('❌ Error updating category:', err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || 'Failed to update category';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchCategories]);

  return {
    categories,
    loading,
    error,
    refetch: fetchCategories,
    reorderCategories,
    deleteCategory,
    createCategory,
    updateCategory,
  };
};

